import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  LogOut, Users, Building2, ClipboardList, Mail, Plus, UserPlus, Shield
} from 'lucide-react';
import MessagesNavButton from '@/components/MessagesNavButton';
import XVAssistWidget from '@/components/XVAssistWidget';
import ModuleStripCard from '@/components/ModuleStripCard';
import BillingCard from '@/components/BillingCard';

const SA_PROVINCES = ["Eastern Cape","Western Cape","Gauteng","KwaZulu-Natal","Limpopo","Mpumalanga","North West","Free State","Northern Cape"];

export default function SchoolDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState({ players: [] });
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');

  const [profileData, setProfileData] = useState({
    name: '', institution_type: 'school', province: '', town: '',
    contact_email: '', contact_phone: '', description: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const dashRes = await api.get('/school/dashboard');
      setStats(dashRes.data);

      if (dashRes.data.has_profile) {
        const [profRes, membRes, teamsRes] = await Promise.all([
          api.get('/school/profile'),
          api.get('/school/members'),
          api.get('/school/teams')
        ]);
        setProfile(profRes.data);
        setMembers(membRes.data);
        setTeams(teamsRes.data);
      } else {
        setShowProfileForm(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setShowProfileForm(true);
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/school/profile', profileData);
      setProfile(res.data);
      setShowProfileForm(false);
      toast.success('Institution profile created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await api.put('/school/profile', profileData);
      setProfile(res.data);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleInvite = async () => {
    const emails = inviteEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
    if (emails.length === 0) { toast.error('Enter at least one email'); return; }
    try {
      await api.post('/school/invite', { emails });
      toast.success(`Invited ${emails.length} player(s)`);
      setInviteEmails('');
      fetchData();
    } catch (error) {
      toast.error('Failed to send invites');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-primary-neon text-xl">Loading dashboard...</div>
      </div>
    );
  }

  if (showProfileForm && !profile) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white" data-testid="school-setup">
        <nav className="border-b border-zinc-800 bg-zinc-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="FutureXV" className="h-10" />
                <span className="text-zinc-600">|</span>
                <span className="font-heading font-bold">School/Club Setup</span>
              </div>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="text-zinc-400 hover:text-white"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
            </div>
          </div>
        </nav>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="bg-zinc-900 border-zinc-800 p-8" data-testid="create-institution-form">
            <h2 className="text-2xl font-heading font-bold mb-6">Set Up Your Institution</h2>
            <form onSubmit={handleCreateProfile} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-zinc-300">Institution Name</Label>
                  <Input value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" required data-testid="institution-name-input" />
                </div>
                <div>
                  <Label className="text-zinc-300">Type</Label>
                  <Select value={profileData.institution_type} onValueChange={(v) => setProfileData({...profileData, institution_type: v})}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" data-testid="institution-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="school">School</SelectItem>
                      <SelectItem value="club">Club</SelectItem>
                      <SelectItem value="university">University</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Province</Label>
                  <Select value={profileData.province} onValueChange={(v) => setProfileData({...profileData, province: v})}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" data-testid="institution-province-select"><SelectValue placeholder="Select province" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      {SA_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Town</Label>
                  <Input value={profileData.town} onChange={(e) => setProfileData({...profileData, town: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" required data-testid="institution-town-input" />
                </div>
                <div>
                  <Label className="text-zinc-300">Contact Email</Label>
                  <Input type="email" value={profileData.contact_email} onChange={(e) => setProfileData({...profileData, contact_email: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" data-testid="institution-email-input" />
                </div>
                <div>
                  <Label className="text-zinc-300">Contact Phone</Label>
                  <Input value={profileData.contact_phone} onChange={(e) => setProfileData({...profileData, contact_phone: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 h-12" data-testid="institution-phone-input" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Description</Label>
                <Textarea value={profileData.description} onChange={(e) => setProfileData({...profileData, description: e.target.value})} className="bg-zinc-950 border-zinc-800 text-white mt-2 min-h-[80px]" data-testid="institution-description-input" />
              </div>
              <Button type="submit" className="btn-primary px-8 py-6" data-testid="create-institution-button">Create Institution Profile</Button>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="school-dashboard">
      <nav className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FutureXV" className="h-10" />
              <span className="text-zinc-600">|</span>
              <span className="font-heading font-bold">{profile?.name || 'School/Club'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-orange-500/10 text-orange-400 border border-orange-500/20">{profile?.institution_type || 'School'}</Badge>
              <Button variant="outline" size="sm" onClick={() => navigate('/playbook')} className="border-zinc-700 text-zinc-300 mr-1" data-testid="playbook-nav-button">Playbook</Button>
              <MessagesNavButton />
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="school-logout-button"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2" data-testid="school-welcome">Welcome, {user?.full_name}</h1>
          <p className="text-zinc-400 font-body">Manage your institution, teams, and players</p>
        </div>

        <BillingCard />

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="school-stats">
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-neon/10 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-primary-neon" /></div>
                <div><p className="text-zinc-400 text-xs">Players</p><p className="text-xl font-heading font-bold">{stats.total_players}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center"><ClipboardList className="w-5 h-5 text-blue-400" /></div>
                <div><p className="text-zinc-400 text-xs">Teams</p><p className="text-xl font-heading font-bold">{stats.total_teams}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center"><Mail className="w-5 h-5 text-yellow-400" /></div>
                <div><p className="text-zinc-400 text-xs">Pending Invites</p><p className="text-xl font-heading font-bold">{stats.pending_invites}</p></div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center"><Mail className="w-5 h-5 text-green-400" /></div>
                <div><p className="text-zinc-400 text-xs">Total Invites</p><p className="text-xl font-heading font-bold">{stats.total_invites}</p></div>
              </div>
            </Card>
          </div>
        )}

        <div className="mb-8"><ModuleStripCard role="school_club" /></div>

        <Tabs defaultValue="players" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8 bg-zinc-900 max-w-lg" data-testid="school-tabs">
            <TabsTrigger value="players" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="players-tab">
              <Users className="w-4 h-4 mr-2" />Players
            </TabsTrigger>
            <TabsTrigger value="invite" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="invite-tab">
              <UserPlus className="w-4 h-4 mr-2" />Invite
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="profile-tab">
              <Building2 className="w-4 h-4 mr-2" />Institution
            </TabsTrigger>
          </TabsList>

          <TabsContent value="players" data-testid="players-content">
            <h2 className="text-2xl font-heading font-bold mb-6">Players at {profile?.name || 'Your Institution'}</h2>
            {members.players.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800 p-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                <p className="text-zinc-500">No players linked to your institution yet. Invite players to join!</p>
              </Card>
            ) : (
              <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="players-table">
                    <thead className="bg-zinc-950 border-b border-zinc-800">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">Name</th>
                        <th className="px-5 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">Position</th>
                        <th className="px-5 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">Age</th>
                        <th className="px-5 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">Gender</th>
                        <th className="px-5 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">Verified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.players.map((item) => (
                        <tr key={item.profile?.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                          <td className="px-5 py-4 text-sm text-white">{item.user?.full_name || '-'}</td>
                          <td className="px-5 py-4 text-sm text-zinc-400">{item.profile?.position || '-'}</td>
                          <td className="px-5 py-4 text-sm text-zinc-400">{item.profile?.age || '-'}</td>
                          <td className="px-5 py-4 text-sm text-zinc-400 capitalize">{item.profile?.gender || '-'}</td>
                          <td className="px-5 py-4">
                            {item.profile?.verified ? (
                              <Badge className="bg-green-500/10 text-green-400 border border-green-500/20"><Shield className="w-3 h-3 mr-1" />Verified</Badge>
                            ) : (
                              <Badge className="bg-zinc-500/10 text-zinc-400 border border-zinc-500/20">Pending</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="invite" data-testid="invite-content">
            <Card className="bg-zinc-900 border-zinc-800 p-8">
              <h2 className="text-2xl font-heading font-bold mb-2">Invite Players</h2>
              <p className="text-zinc-400 mb-6">Enter player email addresses to invite them to register under your institution. Separate multiple emails with commas or new lines.</p>
              <Textarea value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} placeholder="player1@email.com, player2@email.com" className="bg-zinc-950 border-zinc-800 text-white min-h-[120px] mb-4" data-testid="invite-emails-input" />
              <Button onClick={handleInvite} className="btn-primary" data-testid="send-invites-button">
                <Mail className="w-4 h-4 mr-2" />Send Invites
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="profile" data-testid="profile-content">
            <Card className="bg-zinc-900 border-zinc-800 p-8" data-testid="institution-profile-card">
              <h2 className="text-2xl font-heading font-bold mb-6">Institution Profile</h2>
              {profile && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="player-stat-card rounded-lg p-4"><p className="text-zinc-500 text-sm mb-1">Name</p><p className="text-xl font-heading font-bold">{profile.name}</p></div>
                  <div className="player-stat-card rounded-lg p-4"><p className="text-zinc-500 text-sm mb-1">Type</p><p className="text-xl font-heading font-bold capitalize">{profile.institution_type}</p></div>
                  <div className="player-stat-card rounded-lg p-4"><p className="text-zinc-500 text-sm mb-1">Province</p><p className="text-xl font-heading font-bold">{profile.province}</p></div>
                  <div className="player-stat-card rounded-lg p-4"><p className="text-zinc-500 text-sm mb-1">Town</p><p className="text-xl font-heading font-bold">{profile.town}</p></div>
                  {profile.contact_email && <div className="player-stat-card rounded-lg p-4"><p className="text-zinc-500 text-sm mb-1">Contact Email</p><p className="text-xl font-heading font-bold">{profile.contact_email}</p></div>}
                  {profile.contact_phone && <div className="player-stat-card rounded-lg p-4"><p className="text-zinc-500 text-sm mb-1">Contact Phone</p><p className="text-xl font-heading font-bold">{profile.contact_phone}</p></div>}
                  {profile.description && <div className="md:col-span-2 player-stat-card rounded-lg p-4"><p className="text-zinc-500 text-sm mb-1">Description</p><p className="text-zinc-300">{profile.description}</p></div>}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <XVAssistWidget />
    </div>
  );
}
